import BaseDropdown from "@/components/reusables/dropdowns/BaseDropdown";
import { useGetModuleUnitsQuery } from "@/redux/features/units/unitsApi";
import {
    ProductSectionProps,
    ProductType,
} from "@components/Dashboard/pages/Products/components/CreationEditingLayout/types";
import BaseInput from "@reusables/BaseInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ToggleWrapper from "../../ToggleWrapper";
import ServiceHider from "../elements/ServiceHider";
import CategoryDropdown from "./elements/CategoryDropdown";
import LocationDropdown from "./elements/LocationDropdown";
import SupplierDropdown from "./elements/SupplierDropdown";
import SupplierProductCodeField from "./elements/SupplierProductCodeField";
import TaxDropdown from "./elements/TaxDropdown";
import UnitDropdown from "./elements/UnitDropdown";
import { useState } from "react";
import BaseButton from "@/components/reusables/BaseButton";
import BaseCheckbox from "@/components/reusables/BaseCheckbox";

export default function General({ editing }: ProductSectionProps) {
    const { t } = useTranslation("", { keyPrefix: "products.general" });

    const { control } = useFormContext<ProductType>();

    const baseUnit = useWatch({ control, name: "general.unit" });
    const unitEquivalents = useWatch({ control, name: "general.unit_equivalents" });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "general.unit_equivalents",
    });

    // TODO: move this shit to a unit-equivalents related component to reduce rerenders
    const { data: options } = useGetModuleUnitsQuery("product");
    const [isAddingEquivalent, setIsAddingEquivalent] = useState(false);
    const isBaseUnitSelected = Boolean(baseUnit?.id);
    const showUnitEquivalents = isBaseUnitSelected || fields.length > 0;
    const selectedUnitIds = (unitEquivalents ?? [])
        .map((item) => item?.unit_id)
        .filter((id): id is number => Boolean(id));

    const getFilteredOptions = (currentUnitId?: number) => {
        const baseUnitId = baseUnit?.id;
        return (options ?? []).filter((opt) => {
            if (baseUnitId && opt.id === baseUnitId && opt.id !== currentUnitId) {
                return false;
            }

            if (selectedUnitIds.includes(opt.id) && opt.id !== currentUnitId) {
                return false;
            }

            return true;
        });
    };

    return (
        <>
            <BaseInputsGrid>
                {/*  PRODUCT NAME */}
                <Controller
                    name={"general.name"}
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t("fields.productName") + " *"}
                        />
                    )}
                />

                {/*  PRODUCT CODE */}
                <Controller
                    name="general.code"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t("fields.productCode")}
                            disabled={editing}
                        />
                    )}
                />

                {/* 24SevenOffice Supplier Code */}
                <SupplierProductCodeField editing={editing} />

                {/*  BARCODE */}
                <ServiceHider>
                    <ToggleWrapper label={t("fields.barcode")} fieldKey="barcode">
                        <Controller
                            name="general.barcode"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput {...field} error={fieldState.error} />
                            )}
                        />
                    </ToggleWrapper>
                </ServiceHider>

                {/*  CATEGORY */}
                <CategoryDropdown />

                {/* UNIT */}
                <UnitDropdown />

                {/*  DEFAULT LOCATION */}
                {!editing && (
                    <ServiceHider>
                        <LocationDropdown />
                    </ServiceHider>
                )}

                {/*  PRIMARY SUPPLIER */}
                <SupplierDropdown />

                {/*  TAX */}
                <TaxDropdown />
            </BaseInputsGrid>

            {/* EQUIVALENT UNITS */}
            {showUnitEquivalents && (
                <div className="py-[12px] px-[16px] border border-[#D8D8D8] rounded-2xl">
                    <h5 className="text-[#686868] text-sm font-medium">
                        {t("unitEquivalents.header")}
                    </h5>
                    <div className="flex gap-2">
                        <div className={`flex flex-col gap-[8px] w-full `}>
                            {fields.map((unit, index) => (
                                <div key={unit.id} className="flex items-center gap-[8px] ">
                                    <div className="flex gap-[8px]">
                                        <div style={{ width: "100px" }}>
                                            <Controller
                                                name={`general.unit_equivalents.${index}.quantity`}
                                                control={control}
                                                render={({ field: f, fieldState }) => (
                                                    <BaseInput
                                                        {...f}
                                                        error={fieldState.error}
                                                        type="number"
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div style={{ width: "200px" }}>
                                            <Controller
                                                name={`general.unit_equivalents.${index}`}
                                                render={({ field, fieldState }) => (
                                                    <BaseDropdown
                                                        error={fieldState.error}
                                                        options={getFilteredOptions(
                                                            field.value.unit_id
                                                        )}
                                                        value={field.value.unit_id}
                                                        onChange={(newItem) => {
                                                            field.onChange({
                                                                ...field.value,
                                                                unit_id: newItem?.id ?? 0,
                                                                unitCode: newItem?.code ?? "",
                                                            });
                                                        }}
                                                        getter={{
                                                            key: (opt) => opt.id,
                                                            label: (opt) => opt.name,
                                                            caption: (opt) => opt.code ?? opt.id,
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {isAddingEquivalent && index === fields.length - 1 && (
                                        <>
                                            <BaseButton
                                                size="xs"
                                                text={t("unitEquivalents.save")}
                                                color="transparent"
                                                textColor="#7556FA"
                                                style={{ backgroundColor: "#D9DAFD" }}
                                                buttonWidth="75px"
                                                className="font-semibold"
                                                type="button"
                                                onClick={() => setIsAddingEquivalent(false)}
                                            />

                                            <BaseButton
                                                size="xs"
                                                text={t("unitEquivalents.cancel")}
                                                color="transparent"
                                                textColor="#686868"
                                                buttonWidth="75px"
                                                className="font-semibold"
                                                type="button"
                                                onClick={() => {
                                                    remove(index);
                                                    setIsAddingEquivalent(false);
                                                }}
                                            />
                                        </>
                                    )}

                                    {!isAddingEquivalent && (
                                        <div className="w-fit">
                                            <BaseButton
                                                size="xs"
                                                text={t("unitEquivalents.delete")}
                                                color="#F4F4F4"
                                                textColor="#686868"
                                                onClick={() => remove(index)}
                                                buttonWidth="75px"
                                                className="font-semibold"
                                            />
                                        </div>
                                    )}
                                    <div className="w-full flex justify-end">
                                        <Controller
                                            name={`general.unit_equivalents.${index}.displayWithBaseUnit`}
                                            render={({ field, fieldState }) => (
                                                <BaseCheckbox
                                                    label={t("unitEquivalents.checkbox")}
                                                    error={fieldState.error}
                                                    value={Boolean(field.value)}
                                                    onChange={(checked) => field.onChange(checked)}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            ))}
                            {fields.length === 0 && !isAddingEquivalent && (
                                <div className="w-fit">
                                    <BaseButton
                                        size="md"
                                        text={t("unitEquivalents.button")}
                                        color="#F4F4F4"
                                        textColor="#686868"
                                        iconPos="left"
                                        disabled={!isBaseUnitSelected}
                                        onClick={() => {
                                            append({
                                                quantity: 0,
                                                unit_id: 0,
                                                displayWithBaseUnit: false,
                                                unitCode: "",
                                            });
                                            setIsAddingEquivalent(true);
                                        }}
                                    ></BaseButton>
                                </div>
                            )}
                            {fields.length > 0 && !isAddingEquivalent && (
                                <div className="w-fit">
                                    <BaseButton
                                        size="md"
                                        text={t("unitEquivalents.button")}
                                        color="#F4F4F4"
                                        textColor="#686868"
                                        iconPos="left"
                                        disabled={!isBaseUnitSelected}
                                        onClick={() => {
                                            append({
                                                quantity: 0,
                                                unit_id: 0,
                                                displayWithBaseUnit: false,
                                                unitCode: "",
                                            });
                                            setIsAddingEquivalent(true);
                                        }}
                                    ></BaseButton>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
